import React, { useEffect, useState } from 'react'
import { Box, Divider, Typography } from '@mui/material'
import { Table } from 'react-bootstrap'
import { BASE_BACKEND_URL, fetchWithAuth } from '../../auth'
import {
  UI_COLOR_BORDER_DIVIDER,
  UI_COLOR_BORDER_FAINT,
  UI_COLOR_BORDER_MUTED,
  UI_COLOR_PRIMARY,
  UI_COLOR_SHADOW_CARD,
  UI_COLOR_TEXT_BODY_DARK,
  UI_COLOR_TEXT_MUTED_ALT,
  UI_COLOR_TEXT_SECTION,
  UI_COLOR_TEXT_SUMMARY_LABEL,
  UI_COLOR_WHITE
} from '../../constants/uiColors'
import {
  UI_TEXT_EMPTY_VALUE,
  UI_TEXT_LOADING,
  UI_TEXT_NO_RESULTS
} from '../../constants/uiText'
import LoadingScreen from '../../components/LoadingScreen'
import { REFUND_APPROVAL_TEXT } from '../constants/refundApprovalText'
import dayjs from '../../utils/dayjsConfig'

const HISTORY_TABLE_COLUMNS = 6

const formatAuditTimestamp = (value) => {
  if (!value) return UI_TEXT_EMPTY_VALUE
  return dayjs(value).local().format('DD/MM/YYYY, h:mm A')
}

const isRefundRejectionChange = (changeType = '') =>
  /rechazo/i.test(String(changeType || ''))

const getEntryRejectionReason = (entry, rejectionReason = '') => {
  if (!isRefundRejectionChange(entry?.change_type)) return UI_TEXT_EMPTY_VALUE

  const trimmedReason = String(rejectionReason || '').trim()
  return trimmedReason || UI_TEXT_EMPTY_VALUE
}

const HistoryDetailRow = ({ label, value, muted = false }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, auto)',
      alignItems: 'baseline',
      gap: 1.25,
      py: 0.4
    }}
  >
    <Typography
      component="span"
      sx={{ fontSize: 13, fontWeight: 700, color: UI_COLOR_TEXT_SUMMARY_LABEL, lineHeight: 1.35 }}
    >
      {label}
    </Typography>
    <Typography
      component="span"
      sx={{
        maxWidth: '52vw',
        fontSize: 13,
        fontWeight: 800,
        color: muted ? UI_COLOR_TEXT_MUTED_ALT : UI_COLOR_TEXT_BODY_DARK,
        lineHeight: 1.35,
        textAlign: 'right',
        overflowWrap: 'anywhere'
      }}
    >
      {value || UI_TEXT_EMPTY_VALUE}
    </Typography>
  </Box>
)

const HistoryCardDivider = () => (
  <Box sx={{ height: '1px', backgroundColor: UI_COLOR_BORDER_FAINT, my: 1.15 }} />
)

const HistoryMobileCard = ({ entry, rejectionReason }) => {
  const entryRejectionReason = getEntryRejectionReason(entry, rejectionReason)

  return (
    <Box
      sx={{
        backgroundColor: UI_COLOR_WHITE,
        border: `1px solid ${UI_COLOR_BORDER_MUTED}`,
        borderRadius: '8px',
        p: 2,
        mb: 1.5,
        boxShadow: UI_COLOR_SHADOW_CARD
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 800,
          color: UI_COLOR_TEXT_BODY_DARK,
          lineHeight: 1.25,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
        title={entry?.actor_email || UI_TEXT_EMPTY_VALUE}
      >
        {entry?.actor_email || UI_TEXT_EMPTY_VALUE}
      </Typography>
      <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: UI_COLOR_PRIMARY, mt: 0.25 }}>
        {formatAuditTimestamp(entry?.created_at)}
      </Typography>

      <HistoryCardDivider />

      <HistoryDetailRow
        label={REFUND_APPROVAL_TEXT.historyChangeType}
        value={entry?.change_type}
      />

      <HistoryCardDivider />

      <HistoryDetailRow
        label={REFUND_APPROVAL_TEXT.historyPrevious}
        value={entry?.previous_value}
        muted={!entry?.previous_value}
      />
      <HistoryDetailRow label={REFUND_APPROVAL_TEXT.historyNew} value={entry?.new_value} />
      <HistoryDetailRow
        label={REFUND_APPROVAL_TEXT.historyRejectionReason}
        value={entryRejectionReason}
        muted={entryRejectionReason === UI_TEXT_EMPTY_VALUE}
      />
    </Box>
  )
}

const HistoryMobileStateCard = ({ children }) => (
  <Box
    sx={{
      backgroundColor: UI_COLOR_WHITE,
      border: `1px solid ${UI_COLOR_BORDER_MUTED}`,
      borderRadius: '8px',
      p: 2,
      boxShadow: UI_COLOR_SHADOW_CARD
    }}
  >
    {typeof children === 'string' ? (
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: UI_COLOR_TEXT_BODY_DARK }}>
        {children}
      </Typography>
    ) : (
      children
    )}
  </Box>
)

const RefundChronologicalHistoryTab = ({
  refundUuid,
  rejectionReason = '',
  mobileCards = false
}) => {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!refundUuid) {
      setEntries([])
      setLoading(false)
      return undefined
    }

    const abortController = new AbortController()

    const fetchHistory = async () => {
      setLoading(true)
      setError(null)

      try {
        const response = await fetchWithAuth(
          `${BASE_BACKEND_URL}/refunds/${refundUuid}/change_events`,
          {
            method: 'GET',
            signal: abortController.signal
          }
        )

        if (abortController.signal.aborted) return

        if (!response) {
          setError(REFUND_APPROVAL_TEXT.historyLoadError)
          setEntries([])
          return
        }

        setEntries(Array.isArray(response.result) ? response.result : [])
      } catch (err) {
        if (abortController.signal.aborted || err?.name === 'AbortError') return
        setError(REFUND_APPROVAL_TEXT.historyFetchError)
        setEntries([])
      } finally {
        if (!abortController.signal.aborted) setLoading(false)
      }
    }

    fetchHistory()

    return () => abortController.abort()
  }, [refundUuid])

  return (
    <Box>
      <Typography
        sx={{
          fontSize: mobileCards ? 18 : 16,
          lineHeight: mobileCards ? 1.35 : '70px',
          mb: mobileCards ? 2 : 0
        }}
      >
        {REFUND_APPROVAL_TEXT.historyTitle}
      </Typography>

      <Divider
        sx={{
          flexBasis: '100%',
          width: '100%',
          border: 0,
          borderTop: `1px solid ${UI_COLOR_BORDER_DIVIDER}`,
          mb: mobileCards ? 2.5 : 2
        }}
      />

      {!mobileCards && (
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: UI_COLOR_TEXT_SECTION, mb: 2 }}>
          {REFUND_APPROVAL_TEXT.historyChronological}
        </Typography>
      )}

      {mobileCards ? (
        <>
          {loading && <LoadingScreen text={UI_TEXT_LOADING} minHeight="320px" />}

          {!loading && error && <HistoryMobileStateCard>{error}</HistoryMobileStateCard>}

          {!loading && !error && entries.length === 0 && (
            <HistoryMobileStateCard>{UI_TEXT_NO_RESULTS}</HistoryMobileStateCard>
          )}

          {!loading &&
            !error &&
            entries.map((entry) => (
              <HistoryMobileCard
                key={entry?.id || `${entry?.created_at}-${entry?.change_type}`}
                entry={entry}
                rejectionReason={rejectionReason}
              />
            ))}
        </>
      ) : (
        <Table hover responsive>
          <thead className="table-header-gray">
            <tr>
              <th className="vertical-align-middle" style={{ padding: '10px 25px' }}>
                <span className="th-header-text">{REFUND_APPROVAL_TEXT.historyColDate}</span>
              </th>
              <th className="vertical-align-middle">
                <span className="th-header-text">{REFUND_APPROVAL_TEXT.historyColUser}</span>
              </th>
              <th className="vertical-align-middle">
                <span className="th-header-text">{REFUND_APPROVAL_TEXT.historyColChangeType}</span>
              </th>
              <th className="vertical-align-middle">
                <span className="th-header-text">{REFUND_APPROVAL_TEXT.historyColPrevious}</span>
              </th>
              <th className="vertical-align-middle">
                <span className="th-header-text">{REFUND_APPROVAL_TEXT.historyColNew}</span>
              </th>
              <th className="vertical-align-middle">
                <span className="th-header-text">{REFUND_APPROVAL_TEXT.historyColRejectionReason}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={HISTORY_TABLE_COLUMNS} className="td-text-no-border">
                  <LoadingScreen text={UI_TEXT_LOADING} minHeight="320px" />
                </td>
              </tr>
            )}

            {!loading && error && (
              <tr>
                <td colSpan={HISTORY_TABLE_COLUMNS} className="td-text-no-border">
                  {error}
                </td>
              </tr>
            )}

            {!loading && !error && entries.length === 0 && (
              <tr>
                <td colSpan={HISTORY_TABLE_COLUMNS} className="td-text-no-border">
                  {UI_TEXT_NO_RESULTS}
                </td>
              </tr>
            )}

            {!loading &&
              !error &&
              entries.map((entry) => (
                <tr key={entry?.id || `${entry?.created_at}-${entry?.change_type}`}>
                  <td className="td-text-limited td-text-limited-space">
                    {formatAuditTimestamp(entry?.created_at)}
                  </td>
                  <td className="td-text-limited">{entry?.actor_email || UI_TEXT_EMPTY_VALUE}</td>
                  <td className="td-text-limited">{entry?.change_type || UI_TEXT_EMPTY_VALUE}</td>
                  <td className="td-text-limited">
                    {entry?.previous_value || UI_TEXT_EMPTY_VALUE}
                  </td>
                  <td className="td-text-limited">{entry?.new_value || UI_TEXT_EMPTY_VALUE}</td>
                  <td className="td-text-limited">
                    {getEntryRejectionReason(entry, rejectionReason)}
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      )}
    </Box>
  )
}

export default RefundChronologicalHistoryTab
